import { documentObject, FOCUSABLE, windowObject } from '@fold-dev/core'
import * as Token from '@fold-dev/design/tokens-es6'
import { useEffect } from 'react'
import { FRONTEER_FILTERS } from '../components'
import { COLORS, NODE_ENV, NOTIFICATION_ACTION, POSITION_INCREMENT, PROJECT_NOTIFICATIONS } from '../constants'
import { WebsocketService } from '../contexts/websocket.context'
import { FRONTEER_JWT } from '../hooks/auth.hook'
import { useAppStore } from '../store/app'

const FRONTEER_FAST_CACHE = 'FRONTEER_FAST_CACHE'
window[FRONTEER_FAST_CACHE] = {}

export function scrollToCenter(container, target) {
    const containerRect = container.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()

    const scrollLeft = target.offsetLeft - container.offsetWidth / 2 + target.offsetWidth / 2
    const scrollTop = target.offsetTop - container.offsetHeight / 2 + target.offsetHeight / 2

    container.scrollTo({
        left: scrollLeft,
        top: scrollTop,
        behavior: 'smooth', // Optional for smooth scrolling
    })
}

export function hexToOklchHue(hex) {
    // Remove # if present
    hex = hex.replace('#', '')

    // Parse hex to RGB
    const r = parseInt(hex.substr(0, 2), 16) / 255
    const g = parseInt(hex.substr(2, 2), 16) / 255
    const b = parseInt(hex.substr(4, 2), 16) / 255

    // Convert RGB to linear RGB
    const toLinear = (c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))
    const lr = toLinear(r)
    const lg = toLinear(g)
    const lb = toLinear(b)

    // Convert linear RGB to OKLab
    const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb
    const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb
    const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb

    const l_ = Math.cbrt(l)
    const m_ = Math.cbrt(m)
    const s_ = Math.cbrt(s)

    const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_
    const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_
    const b_ = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_

    // Convert OKLab to OKLCH (get hue)
    let hue = Math.atan2(b_, a) * (180 / Math.PI)

    // Normalize hue to 0-360 range
    if (hue < 0) {
        hue += 360
    }

    // Handle achromatic colors (when a and b are both very close to 0)
    if (Math.abs(a) < 1e-10 && Math.abs(b_) < 1e-10) {
        return null // No hue for achromatic colors
    }

    return String(hue)
}

export function hexToAdjustedHSL(hex, amount = 50) {
    let r = 0,
        g = 0,
        b = 0

    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16)
        g = parseInt(hex[2] + hex[2], 16)
        b = parseInt(hex[3] + hex[3], 16)
    } else if (hex.length === 7) {
        r = parseInt(hex.slice(1, 3), 16)
        g = parseInt(hex.slice(3, 5), 16)
        b = parseInt(hex.slice(5, 7), 16)
    }

    r /= 255
    g /= 255
    b /= 255

    const max = Math.max(r, g, b),
        min = Math.min(r, g, b)
    let h = 0,
        s = 0,
        l = (max + min) / 2

    if (max !== min) {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0)
                break
            case g:
                h = (b - r) / d + 2
                break
            case b:
                h = (r - g) / d + 4
                break
        }
        h *= 60
    }

    s = Math.round(s * 100)
    l = Math.round(l * 100)

    // If background is light, return a darker color
    const adjustedL = l > 50 ? Math.max(0, l - amount) : Math.min(100, l + amount)

    return `hsl(${Math.round(h)}, ${s}%, ${adjustedL}%)`
}

export const initFastCache = () => {
    try {
        const cache = localStorage.getItem(FRONTEER_FAST_CACHE) || '{}'
        window[FRONTEER_FAST_CACHE] = JSON.parse(cache)
        logger('init fast cache', window[FRONTEER_FAST_CACHE])
    } catch (e) {
        console.warn('cloud not init fast cache', e)
    }
}

export const setFastCache = (key, data) => {
    try {
        if (!!key) {
            window[FRONTEER_FAST_CACHE] = {
                ...window[FRONTEER_FAST_CACHE],
                [key]: data,
            }
            localStorage.setItem(FRONTEER_FAST_CACHE, JSON.stringify(window[FRONTEER_FAST_CACHE]))
        } else {
            logger('set fast cache: unable, no key')
        }
    } catch (e) {
        console.warn('error setting fast cache', e)
    }
}

export const getFastCache = (key) => {
    if (!key) return []
    logger('get fast cache: ', key, window[FRONTEER_FAST_CACHE][key])
    return window[FRONTEER_FAST_CACHE][key] ?? []
}

export const shallowEqual = (a, b) => {
    if (a === b) return true
    if (typeof a !== 'object' || typeof b !== 'object' || a == null || b == null) return false

    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)
    if (aKeys.length !== bKeys.length) return false

    return aKeys.every((key) => b.hasOwnProperty(key) && a[key] === b[key])
}

export const scrollDown = (div: any) => {
    if (div) {
        div.scrollTo({
            top: div.scrollHeight,
            behavior: 'smooth',
        })
    }
}

export const isString = (e) => typeof e == 'string'

export const openLinkInText = (e, cb) => {
    if (e.target.tagName === 'A' && !!e.target.href) {
        stopEvent(e)
        window.open(e.target.href, '_blank')
    } else {
        cb()
    }
}

export const initCache = (projects, index = 0) => {
    logger('TODO: cache sections')
}

export const getCorrectParentId = (projects, target) => {
    for (let i = target.index - 1; i >= 0; i--) {
        if (projects[i].indent < target.indent) {
            return projects[i].id
        }
    }

    return null
}

export function sortHierarchy(projects) {
    function buildLevel(currentParentId, indent) {
        const children = projects.filter((project) => project.parentId == currentParentId)

        children.sort(byPosition)

        return children.flatMap((project) => {
            const isParent = projects.some((p) => p.parentId === project.id)

            const processedProject = {
                ...project,
                indent: indent,
                isParent: isParent,
            }

            const descendants = buildLevel(project.id, indent + 1)

            return [processedProject, ...descendants]
        })
    }

    return buildLevel(null, 0)
}

export function sortHierarchyOld(projects) {
    return projects
        .filter((project) => !project.parentId)
        .map((project) => ({
            ...project,
            isParent: projects.some((p) => p.parentId == project.id),
            indent: 0,
        }))
        .sort(byPosition)
        .reduce((items, project) => {
            return [
                ...items,
                project,
                ...projects
                    .filter((p) => p.parentId == project.id)
                    .map((p) => ({ ...p, isParent: false, indent: 1 }))
                    .sort(byPosition),
            ]
        }, [])
}

export const getCorrectPositionForTargetIndexWithParent = (projects, target) => {
    let position = 0
    const previousProject = projects[target.index - 1]
    const nextProject = projects[target.index]

    if (!projects.length) return generateRandomPosition()

    if (target.index == 0 && !target.focus) {
        position = +projects[0].position - POSITION_INCREMENT
    } else if (target.index == projects.length && !target.focus) {
        position = +projects[projects.length - 1].position + POSITION_INCREMENT
    } else {
        if (target.focus) {
            const nextNextProject = projects[target.index + 1]
            position = nextNextProject ? +nextNextProject.position - POSITION_INCREMENT : POSITION_INCREMENT
        } else if (nextProject?.parentId == previousProject?.id) {
            position = +nextProject.position - POSITION_INCREMENT
        } else {
            position = (+nextProject.position - +previousProject.position) / 2 + +previousProject.position
        }
    }

    return Math.round(position)
}

export const isRichTextBlank = (text) => {
    return (text || '').trim() == '' || (text || '').trim() == '<p></p>'
}

export function replaceHTMLEntities(str) {
    const htmlEntities = {
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&quot;': '"',
        '&#039;': "'",
        '&ndash;': '-',
    }

    return str.replace(/&[\w#]+;/g, (entity) => {
        return htmlEntities[entity] || entity
    })
}

export function extractUrlFromHtml(htmlContent) {
    const doc = new DOMParser().parseFromString(htmlContent, 'text/html')
    const anchor = doc.querySelector('a')
    return anchor ? anchor.href : null
}

export const containsSameUsers = (arr1, arr2) => {
    if (!arr1 || !arr2) return false
    if (arr1.length !== arr2.length) return false

    const set1 = new Set(arr1.map((obj) => obj.id))
    const set2 = new Set(arr2.map((obj) => obj.id))

    return set1.size === set2.size && [...set1].every((id) => set2.has(id))
}

export function searchLabels(query) {
    const labels = useAppStore.getState().labels
    return labels
        .filter((item) => item.text.toLowerCase().includes(query.toLowerCase()))
        .filter((_, index) => index <= 20)
}

export const getInitials = (projectUsers, userId) => {
    return (projectUsers || [])
        .filter((pu) => pu.id != userId)
        .map((pu) => pu.name.split(' ')[0])
        .join(', ')
}

export const addMeToUsers = (users = [], me) => {
    if (users.findIndex(({ id }) => id == me) == -1) {
        return [...users, me]
    } else {
        return users
    }
}

export function setThemeVariable(variable, value) {
    document.documentElement.style.setProperty(variable, value)
}

export function isEmoticon(content) {
    const text = content.replace('<p>', '').replace('</p>', '').replace('<p></p>', '').trim()

    // Regular expression for text-based emoticons
    const textEmoticonRegex = /^[:;=][\-~]?[)DPOo/\\|]$/

    // Regular expression for Unicode emojis
    const emojiRegex =
        /^[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{1F1E6}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}]+$/u

    // Check if the text matches any of the patterns
    return textEmoticonRegex.test(text) || emojiRegex.test(text)
}

export const getRandomColor = () => {
    return COLORS[Math.floor(Math.random() * COLORS.length)]
}

export const useDocumentTitle = (title) => {
    const workspace = useAppStore((state) => state.workspace)
    const task = useAppStore((state) => state.task)

    useEffect(() => {
        if (title) {
            if (!!workspace.name && title && !task.title) {
                document.title = `${workspace.name} - ${title}`
            } else if (!!workspace.name && title && !!task.title) {
                // skip the title if it's a task, TODO: decide about this
                // document.title = `${workspace.name} - ${title} - ${task.title}`
                document.title = `${workspace.name} - ${task.title}`
            } else {
                document.title = title
            }
        }
    }, [workspace.name, task.title, title])
}

export const getNotificationActionDescription = (action) => {
    switch (action) {
        case NOTIFICATION_ACTION.TASK.ASSIGNED:
            return ' has been assigned a task'
        case NOTIFICATION_ACTION.TASK.COMMENT:
            return ' has left a comment'
        case NOTIFICATION_ACTION.TASK.COMMENT_MENTION:
            return ' has mentioned you'
        case NOTIFICATION_ACTION.PROJECT.MESSAGE:
            return ' has sent a messaged'
        case NOTIFICATION_ACTION.PROJECT.MESSAGE_MENTION:
            return ' has mentioned you in a message'
        default:
            return ' has done something'
    }
}

export const getDestination = (notification) => {
    const { action, projectName, taskTitle, actorName, taskComment } = notification
    switch (action) {
        case NOTIFICATION_ACTION.TASK.ASSIGNED:
            return taskTitle
        case NOTIFICATION_ACTION.TASK.COMMENT:
            return taskTitle
        case NOTIFICATION_ACTION.TASK.COMMENT_MENTION:
            return taskTitle
        case NOTIFICATION_ACTION.PROJECT.MESSAGE:
            return projectName
        case NOTIFICATION_ACTION.PROJECT.MESSAGE_MENTION:
            return projectName
        default:
            return taskTitle || projectName || ''
    }
}

export const getNotificationText = (notification) => {
    const { action, projectName, taskTitle, actorName, taskComment } = notification
    return `${actorName} ${getNotificationActionDescription(action)}`
}

export const REACTIONS = {
    thumns_up: '👍',
    heart: '💜',
    smile: '😆',
    surprised: '😯',
    sad: '😥',
    angry: '😡',
}

export const aggregateReactions = (reactions) => {
    // Aggregate reactions
    const aggregated = reactions.reduce((acc, { id, reaction, userId }) => {
        // Initialize the reaction group if it doesn't exist
        if (!acc[reaction]) {
            acc[reaction] = {
                count: 0,
                reactions: [],
            }
        }

        // Increment the count and add the item to the array
        acc[reaction].count += 1
        acc[reaction].reactions.push({ id, reaction, userId })

        return acc
    }, {})

    // Convert the object into an array of [key, value] entries
    const sortedEntries = Object.entries(aggregated).sort(([keyA], [keyB]) => keyA.localeCompare(keyB))

    // Convert the sorted array back into an object
    const sortedAggregated = Object.fromEntries(sortedEntries)

    return sortedAggregated
}

export const getCachedProjectView = (projectId, defaultView = 'list') => {
    return JSON.parse(localStorage.getItem('FRONTEER_PROJECT_VIEWS') || '{}')[projectId] || defaultView
}

export const setCachedProjectView = (projectId, view) => {
    const views = JSON.parse(localStorage.getItem('FRONTEER_PROJECT_VIEWS') || '{}')
    views[projectId] = view
    localStorage.setItem('FRONTEER_PROJECT_VIEWS', JSON.stringify(views))
}

export const getProjectFilters = (projectId) => {
    const filters = localStorage.getItem(FRONTEER_FILTERS) || '[]'
    return JSON.parse(filters)[projectId] || {}
}

export const generateDocumentTitle = (workspaceName, projectName) => {
    return null
}

export const getMentionsfromTipTapJSON = (json) => {
    const { content } = json
    return flattenContentArray(content)
        .filter((c) => c.type == 'mention')
        .map(({ attrs }) => attrs.id)
}

export const stripS3Url = (url) => {
    return url
        .replace('https://qfma2.upcloudobjects.com/files/', '')
        .replace('https://qfma2.upcloudobjects.com/userfiles/', '')
        .replace('https://cdn.fronteer.app/', '')
        .replace('https://824a72da502935c269374eec5ccc2827.r2.cloudflarestorage.com/fronteer-userfiles/', '')
        .replace('https://824a72da502935c269374eec5ccc2827.r2.cloudflarestorage.com/fronteer-public', '')
        .split('?')[0]
}

export const syncState = (roomId, eventName, payload) => {
    WebsocketService.getInstance().emit('sync', {
        roomId,
        eventName,
        payload,
    })
}

export function removeExistingElements(newObjArray = [], existingArrayObject) {
    const existingArray = existingArrayObject || []
    return newObjArray.filter((newObj) => !existingArray.some((existingItem) => existingItem.id === newObj.id))
}

export function mergeUniqueArrays(arr1, arr2, key = 'id') {
    return [...arr1, ...arr2].reduce((acc, current) => {
        const existingItemIndex = acc.findIndex((item) => item[key] === current[key])
        if (existingItemIndex >= 0) {
            acc[existingItemIndex] = current
        } else {
            acc.push(current)
        }
        return acc
    }, [])
}

export const flattenContentArray = (arr) => {
    return arr.reduce((acc, item) => {
        // Add the current item without its 'content' field
        acc.push({ ...item, content: undefined })

        // If there is nested content, recursively flatten and add to accumulator
        if (item.content && item.content.length > 0) {
            acc = acc.concat(flattenContentArray(item.content))
        }

        return acc
    }, [])
}

export const removeURLTask = (url) => {
    const urlObj = new URL(url) // Parse the URL
    const index = urlObj.pathname.indexOf('/task') // Find the position of "/sb"

    // If "/sb" exists in the pathname, slice the pathname before it
    if (index !== -1) {
        urlObj.pathname = urlObj.pathname.slice(0, index)
    }

    // Return the new path starting with "/" along with the original query string
    return urlObj.pathname + urlObj.search
}

export const replaceLastPathValue = (url, newValue) => {
    const urlObj = new URL(url) // Parse the URL
    const paths = urlObj.pathname.split('/').filter(Boolean) // Split and filter empty values
    paths[paths.length - 1] = newValue // Replace the last path segment
    const newPath = '/' + paths.join('/') // Rebuild the pathname

    // Include the search (query string) if it exists
    return newPath + urlObj.search
}

export const urlContainsId = (id, cb) => {
    if (window.location.href.includes(id)) cb()
}

export const generateRandomPosition = () => {
    return Math.round(
        Math.random() * POSITION_INCREMENT +
            Math.random() * POSITION_INCREMENT +
            Math.random() * POSITION_INCREMENT +
            POSITION_INCREMENT
    )
}

export const generateRandomNextPosition = (arr) => {
    const random = generateRandomPosition()
    return !arr.length ? POSITION_INCREMENT : +arr[arr.length - 1].position + random
}

export const addNumberSuffix = (number) => {
    switch (+number) {
        case 0:
            return number + ''
        case 1:
            return number + ''
        case 21:
            return number + 'st'
        case 31:
            return number + 'st'
        case 2:
            return number + 'nd'
        case 22:
            return number + 'nd'
        case 32:
            return number + 'nd'
        case 3:
            return number + 'rd'
        case 23:
            return number + 'rd'
        case 33:
            return number + 'rd'
        default:
            return number + 'th'
    }
}

export const focusOnFirstFosableElement = (container) => {
    const elements = container.querySelectorAll(FOCUSABLE.join(','))
    const firstFocusableElement = elements[0]
    firstFocusableElement?.focus()
}

export const focusOnLastFosableElement = (container) => {
    const elements = container.querySelectorAll(FOCUSABLE.join(','))
    const lastFocusableElement = elements[elements.length - 1]
    lastFocusableElement?.focus()
}

const invalidDate = (date) => isNaN(new Date(date).getTime())

export const getWeekday = (date) => {
    if (invalidDate(date) || !date) return '--'
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
    })
}

export const formatAdaptiveTimestamp = (timestamp, secondsPerSlot, slots) => {
    const date = new Date(timestamp * 1000)

    // Total visible time range
    const totalSpanSeconds = secondsPerSlot * slots

    if (totalSpanSeconds <= 60 * 60 * 2) {
        // ≤ 2 hours
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
        })
    } else if (totalSpanSeconds <= 60 * 60 * 24) {
        // ≤ 1 day
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
        })
    } else if (totalSpanSeconds <= 60 * 60 * 24 * 7) {
        // ≤ 1 week
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
        })
    } else if (totalSpanSeconds <= 60 * 60 * 24 * 30) {
        // ≤ 1 month
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        })
    } else if (totalSpanSeconds <= 60 * 60 * 24 * 90) {
        // ≤ 3 months
        return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        })
    } else {
        // > 3 months
        return date.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric',
        })
    }
}

export const formatTimestamp = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    })
}

export const formatTimestampShort = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    })
}

export const getShortDateFormat = (date) => {
    if (invalidDate(date) || !date) return '--'
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    })
}

export const getDatePickerDateFormat = (date) => {
    if (invalidDate(date) || !date) return '--'
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    })
}

export const getMediumDateFormat = (date) => {
    if (invalidDate(date) || !date) return '--'
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })
}

export const getDateFormat = (date) => {
    if (invalidDate(date) || !date) return '--'
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    })
}

export const getTimeFormat = (date) => {
    if (invalidDate(date) || !date) return '--'
    return date.toLocaleTimeString()
}

export const getDateSelectTimeFormat = (date) => {
    if (invalidDate(date) || !date) return '--'
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
}

export const getShortTimeFormat = (date) => {
    if (invalidDate(date) || !date) return '--'
    return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
    })
}

export const stopEvent = (e) => {
    e.preventDefault()
    e.stopPropagation()
}

export const getLastChar = (str: string = '') => str.charAt(str.length - 1) + ''

export const isOffScreenX = (element, position) => {
    if (!element) return false

    const bounding = element.getBoundingClientRect()
    const left = position.x
    const right = left + bounding.width

    return left < 0 || right > (windowObject.innerWidth || documentObject.documentElement.clientWidth)
}

export const isOffScreenY = (element, position) => {
    if (!element) return false

    const bounding = element.getBoundingClientRect()
    const top = position.y
    const bottom = top + bounding.height

    return top < 0 || bottom > (windowObject.innerHeight || documentObject.documentElement.clientHeight)
}

export const getCustomGhostElement = (title, amount) => {
    return `
        <div class="f-card" style="padding: var(--f-space-inset-x-2); width: fit-content; max-width: 300px;">
            <div class="f-text"><strong>${title}</strong></div>
            ${
                amount == 1
                    ? '<div class="f-text sm">+ 1 other</div>'
                    : amount > 1
                    ? '<div class="f-text sm">+ ' + (amount - 1) + ' others</div>'
                    : ''
            }
        </div>
    `
}

export const setEndOfContentEditable = (contentEditableElement) => {
    let range, selection
    if (documentObject.createRange) {
        range = documentObject.createRange()
        range.selectNodeContents(contentEditableElement)
        range.collapse(false)
        selection = windowObject.getSelection()
        selection.removeAllRanges()
        selection.addRange(range)
    } else if (documentObject.selection) {
        range = documentObject.body.createTextRange()
        range.moveToElementText(contentEditableElement)
        range.collapse(false)
        range.select()
    }
}

export const DEFAULT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const getTimezones = () => {
    return [
        'Europe/Andorra',
        'Asia/Dubai',
        'Asia/Kabul',
        'Europe/Tirane',
        'Asia/Yerevan',
        'Antarctica/Casey',
        'Antarctica/Davis',
        'Antarctica/DumontDUrville', // https://bugs.chromium.org/p/chromium/issues/detail?id=928068
        'Antarctica/Mawson',
        'Antarctica/Palmer',
        'Antarctica/Rothera',
        'Antarctica/Syowa',
        'Antarctica/Troll',
        'Antarctica/Vostok',
        'America/Argentina/Buenos_Aires',
        'America/Argentina/Cordoba',
        'America/Argentina/Salta',
        'America/Argentina/Jujuy',
        'America/Argentina/Tucuman',
        'America/Argentina/Catamarca',
        'America/Argentina/La_Rioja',
        'America/Argentina/San_Juan',
        'America/Argentina/Mendoza',
        'America/Argentina/San_Luis',
        'America/Argentina/Rio_Gallegos',
        'America/Argentina/Ushuaia',
        'Pacific/Pago_Pago',
        'Europe/Vienna',
        'Australia/Lord_Howe',
        'Antarctica/Macquarie',
        'Australia/Hobart',
        'Australia/Currie',
        'Australia/Melbourne',
        'Australia/Sydney',
        'Australia/Broken_Hill',
        'Australia/Brisbane',
        'Australia/Lindeman',
        'Australia/Adelaide',
        'Australia/Darwin',
        'Australia/Perth',
        'Australia/Eucla',
        'Asia/Baku',
        'America/Barbados',
        'Asia/Dhaka',
        'Europe/Brussels',
        'Europe/Sofia',
        'Atlantic/Bermuda',
        'Asia/Brunei',
        'America/La_Paz',
        'America/Noronha',
        'America/Belem',
        'America/Fortaleza',
        'America/Recife',
        'America/Araguaina',
        'America/Maceio',
        'America/Bahia',
        'America/Sao_Paulo',
        'America/Campo_Grande',
        'America/Cuiaba',
        'America/Santarem',
        'America/Porto_Velho',
        'America/Boa_Vista',
        'America/Manaus',
        'America/Eirunepe',
        'America/Rio_Branco',
        'America/Nassau',
        'Asia/Thimphu',
        'Europe/Minsk',
        'America/Belize',
        'America/St_Johns',
        'America/Halifax',
        'America/Glace_Bay',
        'America/Moncton',
        'America/Goose_Bay',
        'America/Blanc-Sablon',
        'America/Toronto',
        'America/Nipigon',
        'America/Thunder_Bay',
        'America/Iqaluit',
        'America/Pangnirtung',
        'America/Atikokan',
        'America/Winnipeg',
        'America/Rainy_River',
        'America/Resolute',
        'America/Rankin_Inlet',
        'America/Regina',
        'America/Swift_Current',
        'America/Edmonton',
        'America/Cambridge_Bay',
        'America/Yellowknife',
        'America/Inuvik',
        'America/Creston',
        'America/Dawson_Creek',
        'America/Fort_Nelson',
        'America/Vancouver',
        'America/Whitehorse',
        'America/Dawson',
        'Indian/Cocos',
        'Europe/Zurich',
        'Africa/Abidjan',
        'Pacific/Rarotonga',
        'America/Santiago',
        'America/Punta_Arenas',
        'Pacific/Easter',
        'Asia/Shanghai',
        'Asia/Urumqi',
        'America/Bogota',
        'America/Costa_Rica',
        'America/Havana',
        'Atlantic/Cape_Verde',
        'America/Curacao',
        'Indian/Christmas',
        'Asia/Nicosia',
        'Asia/Famagusta',
        'Europe/Prague',
        'Europe/Berlin',
        'Europe/Copenhagen',
        'America/Santo_Domingo',
        'Africa/Algiers',
        'America/Guayaquil',
        'Pacific/Galapagos',
        'Europe/Tallinn',
        'Africa/Cairo',
        'Africa/El_Aaiun',
        'Europe/Madrid',
        'Africa/Ceuta',
        'Atlantic/Canary',
        'Europe/Helsinki',
        'Pacific/Fiji',
        'Atlantic/Stanley',
        'Pacific/Chuuk',
        'Pacific/Pohnpei',
        'Pacific/Kosrae',
        'Atlantic/Faroe',
        'Europe/Paris',
        'Europe/London',
        'Asia/Tbilisi',
        'America/Cayenne',
        'Africa/Accra',
        'Europe/Gibraltar',
        'America/Godthab',
        'America/Danmarkshavn',
        'America/Scoresbysund',
        'America/Thule',
        'Europe/Athens',
        'Atlantic/South_Georgia',
        'America/Guatemala',
        'Pacific/Guam',
        'Africa/Bissau',
        'America/Guyana',
        'Asia/Hong_Kong',
        'America/Tegucigalpa',
        'America/Port-au-Prince',
        'Europe/Budapest',
        'Asia/Jakarta',
        'Asia/Pontianak',
        'Asia/Makassar',
        'Asia/Jayapura',
        'Europe/Dublin',
        'Asia/Jerusalem',
        'Asia/Kolkata',
        'Indian/Chagos',
        'Asia/Baghdad',
        'Asia/Tehran',
        'Atlantic/Reykjavik',
        'Europe/Rome',
        'America/Jamaica',
        'Asia/Amman',
        'Asia/Tokyo',
        'Africa/Nairobi',
        'Asia/Bishkek',
        'Pacific/Tarawa',
        'Pacific/Enderbury',
        'Pacific/Kiritimati',
        'Asia/Pyongyang',
        'Asia/Seoul',
        'Asia/Almaty',
        'Asia/Qyzylorda',
        'Asia/Qostanay', // https://bugs.chromium.org/p/chromium/issues/detail?id=928068
        'Asia/Aqtobe',
        'Asia/Aqtau',
        'Asia/Atyrau',
        'Asia/Oral',
        'Asia/Beirut',
        'Asia/Colombo',
        'Africa/Monrovia',
        'Europe/Vilnius',
        'Europe/Luxembourg',
        'Europe/Riga',
        'Africa/Tripoli',
        'Africa/Casablanca',
        'Europe/Monaco',
        'Europe/Chisinau',
        'Pacific/Majuro',
        'Pacific/Kwajalein',
        'Asia/Yangon',
        'Asia/Ulaanbaatar',
        'Asia/Hovd',
        'Asia/Choibalsan',
        'Asia/Macau',
        'America/Martinique',
        'Europe/Malta',
        'Indian/Mauritius',
        'Indian/Maldives',
        'America/Mexico_City',
        'America/Cancun',
        'America/Merida',
        'America/Monterrey',
        'America/Matamoros',
        'America/Mazatlan',
        'America/Chihuahua',
        'America/Ojinaga',
        'America/Hermosillo',
        'America/Tijuana',
        'America/Bahia_Banderas',
        'Asia/Kuala_Lumpur',
        'Asia/Kuching',
        'Africa/Maputo',
        'Africa/Windhoek',
        'Pacific/Noumea',
        'Pacific/Norfolk',
        'Africa/Lagos',
        'America/Managua',
        'Europe/Amsterdam',
        'Europe/Oslo',
        'Asia/Kathmandu',
        'Pacific/Nauru',
        'Pacific/Niue',
        'Pacific/Auckland',
        'Pacific/Chatham',
        'America/Panama',
        'America/Lima',
        'Pacific/Tahiti',
        'Pacific/Marquesas',
        'Pacific/Gambier',
        'Pacific/Port_Moresby',
        'Pacific/Bougainville',
        'Asia/Manila',
        'Asia/Karachi',
        'Europe/Warsaw',
        'America/Miquelon',
        'Pacific/Pitcairn',
        'America/Puerto_Rico',
        'Asia/Gaza',
        'Asia/Hebron',
        'Europe/Lisbon',
        'Atlantic/Madeira',
        'Atlantic/Azores',
        'Pacific/Palau',
        'America/Asuncion',
        'Asia/Qatar',
        'Indian/Reunion',
        'Europe/Bucharest',
        'Europe/Belgrade',
        'Europe/Kaliningrad',
        'Europe/Moscow',
        'Europe/Simferopol',
        'Europe/Kirov',
        'Europe/Astrakhan',
        'Europe/Volgograd',
        'Europe/Saratov',
        'Europe/Ulyanovsk',
        'Europe/Samara',
        'Asia/Yekaterinburg',
        'Asia/Omsk',
        'Asia/Novosibirsk',
        'Asia/Barnaul',
        'Asia/Tomsk',
        'Asia/Novokuznetsk',
        'Asia/Krasnoyarsk',
        'Asia/Irkutsk',
        'Asia/Chita',
        'Asia/Yakutsk',
        'Asia/Khandyga',
        'Asia/Vladivostok',
        'Asia/Ust-Nera',
        'Asia/Magadan',
        'Asia/Sakhalin',
        'Asia/Srednekolymsk',
        'Asia/Kamchatka',
        'Asia/Anadyr',
        'Asia/Riyadh',
        'Pacific/Guadalcanal',
        'Indian/Mahe',
        'Africa/Khartoum',
        'Europe/Stockholm',
        'Asia/Singapore',
        'America/Paramaribo',
        'Africa/Juba',
        'Africa/Sao_Tome',
        'America/El_Salvador',
        'Asia/Damascus',
        'America/Grand_Turk',
        'Africa/Ndjamena',
        'Indian/Kerguelen',
        'Asia/Bangkok',
        'Asia/Dushanbe',
        'Pacific/Fakaofo',
        'Asia/Dili',
        'Asia/Ashgabat',
        'Africa/Tunis',
        'Pacific/Tongatapu',
        'Europe/Istanbul',
        'America/Port_of_Spain',
        'Pacific/Funafuti',
        'Asia/Taipei',
        'Europe/Kiev',
        'Europe/Uzhgorod',
        'Europe/Zaporozhye',
        'Pacific/Wake',
        'America/New_York',
        'America/Detroit',
        'America/Kentucky/Louisville',
        'America/Kentucky/Monticello',
        'America/Indiana/Indianapolis',
        'America/Indiana/Vincennes',
        'America/Indiana/Winamac',
        'America/Indiana/Marengo',
        'America/Indiana/Petersburg',
        'America/Indiana/Vevay',
        'America/Chicago',
        'America/Indiana/Tell_City',
        'America/Indiana/Knox',
        'America/Menominee',
        'America/North_Dakota/Center',
        'America/North_Dakota/New_Salem',
        'America/North_Dakota/Beulah',
        'America/Denver',
        'America/Boise',
        'America/Phoenix',
        'America/Los_Angeles',
        'America/Anchorage',
        'America/Juneau',
        'America/Sitka',
        'America/Metlakatla',
        'America/Yakutat',
        'America/Nome',
        'America/Adak',
        'Pacific/Honolulu',
        'America/Montevideo',
        'Asia/Samarkand',
        'Asia/Tashkent',
        'America/Caracas',
        'Asia/Ho_Chi_Minh',
        'Pacific/Efate',
        'Pacific/Wallis',
        'Pacific/Apia',
        'Africa/Johannesburg',
    ]
}

export const setFavicon = (color: string) => {
    const favicon: string = `
        <svg 
            width="100%" 
            height="100%" 
            xmlns="http://www.w3.org/2000/svg"
            fillRule="evenodd"
            strokeLinejoin="round"
            strokeMiterlimit="2"
            clipRule="evenodd"
            viewBox="0 0 180 130">
            <g transform="matrix(2.70817,0,-0.447502,2.70817,-684.812,-1393.36)">
                <path fill="${color}" stroke="${color} "strokeWidth={1} d="M373.968,514.499L386.641,549.318L359.198,549.318L363.957,562.394L359.256,562.341L341.843,514.499L373.968,514.499ZM390.159,526.765L382.2,526.765L380.607,522.365L394.796,522.365L396.86,539.224L408.157,557.232L373.16,557.232L371.559,552.832L398.976,552.832L392.378,540.671L390.159,526.765ZM370.887,518.899L348.127,518.899L357.597,544.918L380.357,544.918L370.887,518.899Z" />
            </g>
        </svg>
    `
    const frag = document.createRange().createContextualFragment(favicon)
    const xml = new XMLSerializer().serializeToString(frag)
    const encoded = window.btoa(xml)
    const src = 'data:image/svg+xml;base64,' + encoded
    const link: any = document.querySelector("link[rel*='icon']") || document.createElement('link')

    link.href = src
    link.type = 'image/x-icon'
    link.rel = 'shortcut icon'
}

export const getNewPositionsForNow = (amt) => {
    const startPosition = new Date().getTime()

    return new Array(amt).fill(null).map((_, i) => startPosition + getPositionIncrement() * i)
}

export const getPositionIncrement = () => {
    const random = Math.round(Math.random() * 10000)
    return random + POSITION_INCREMENT
}

const getCorrectPositionsForIndents = (arr, targetIndex, targetIndent) => {
    let prev = null
    let next = null

    // previous sibling
    for (let i = targetIndex - 1; i >= 0; i--) {
        if (arr[i].indent === targetIndent) {
            prev = arr[i]
            break
        }
    }

    // next sibling
    for (let i = targetIndex; i < arr.length; i++) {
        if (arr[i].indent < targetIndent) {
            break // end of current indent block
        }
        if (arr[i].indent === targetIndent) {
            next = arr[i]
            break
        }
    }

    const startPosition = prev ? +prev.position : 0
    const endPosition = next ? +next.position : startPosition + getPositionIncrement()

    return {
        startPosition,
        endPosition,
    }
}

export function getNewPositionsForTasks(arr, targetIndex, targetIndent, amt) {
    let startPosition = 0
    let endPosition = 0

    if (!arr.length) {
        startPosition = generateRandomPosition()
        endPosition = startPosition + amt * getPositionIncrement()
    } else {
        if (targetIndex == 0) {
            startPosition = +arr[0].position - getPositionIncrement()
            endPosition = +arr[0].position
        } else {
            const positions = getCorrectPositionsForIndents(arr, targetIndex, targetIndent)
            startPosition = positions.startPosition
            endPosition = positions.endPosition
        }
    }

    logger(startPosition, endPosition, amt)

    return getPositionsInRange(startPosition, endPosition, amt)
}

export const getPositionsInRange = (startPosition, endPosition, amt) => {
    const isReverse = startPosition > endPosition
    const range = isReverse ? startPosition - endPosition : endPosition - startPosition

    // also can happen with dragging across projects
    const increment =
        startPosition == endPosition ? +Number(range / (amt + 1)).toFixed(4) : Math.round(range / (amt + 1))

    // if it's the reverse then we start from the bottom
    // TODO: there might be an edge case here that reverses multiple tasks being dragged
    return isReverse
        ? new Array(amt)
              .fill(null)
              .map((_, i) => endPosition - increment * (i + 1))
              .reverse()
        : new Array(amt).fill(null).map((_, i) => startPosition + increment * (i + 1))
}

export const getNewPositionsForTargetIndex = (arr, targetIndex, amt) => {
    let startPosition = 0
    let endPosition = 0

    if (!arr.length) {
        startPosition = generateRandomPosition()
        endPosition = startPosition + amt * getPositionIncrement()
    } else {
        if (targetIndex == 0) {
            startPosition = +arr[0].position - getPositionIncrement()
            endPosition = +arr[0].position
        } else if (targetIndex == arr.length) {
            startPosition = +arr[arr.length - 1].position
            endPosition = +arr[arr.length - 1].position + getPositionIncrement()
        } else {
            startPosition = +arr[targetIndex - 1].position
            endPosition = +arr[targetIndex].position
        }
    }

    return getPositionsInRange(startPosition, endPosition, amt)
}

export const getNewPositionForTargetIndex = (arr, targetIndex) => {
    let position = null

    if (!arr.length) return generateRandomPosition()

    if (targetIndex == 0) {
        position = +arr[0].position - getPositionIncrement()
    } else if (targetIndex == arr.length) {
        position = +arr[arr.length - 1].position + getPositionIncrement()
    } else {
        position = (+arr[targetIndex].position - +arr[targetIndex - 1].position) / 2 + +arr[targetIndex - 1].position
    }

    return Math.round(position)
}

export const byOrder = (a, b) => a.order - b.order

export const byLikes = (a, b) => a.likes - b.likes

export const byPosition = (a, b) => a.position - b.position

export const byStart = (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()

export const byEnd = (a, b) => new Date(a.end).getTime() - new Date(b.end).getTime()

const priorities = {
    low: 1,
    medium: 2,
    high: 3,
}

export const byPriority = (a, b) => (priorities[b.priority] || 0) - (priorities[a.priority] || 0)

export const byName = (a, b) => a.name.localeCompare(b.name)

export const byText = (a, b) => a.text.localeCompare(b.text)

export const byTitle = (a, b) => a.title.localeCompare(b.title)

export const byEmail = (a, b) => a.email.localeCompare(b.email)

export const byCreateAt = (a, b) => new Date(a.createdAt) > new Date(b.createdAt)

export const byUpdatedAt = (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()

export const byCreatedAt = (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()

export const plural = (qty, str) => (qty == 1 ? str : str + 's')

export const ABORT_CONTROLLER = 'ABORT_CONTROLLER'
window[ABORT_CONTROLLER] = null

export const fetched: any = async ({ url, method, body, query, abortable }) => {
    try {
        const token = localStorage.getItem(FRONTEER_JWT)
        const headers: any = { 'Content-Type': 'application/json' }
        let signal = undefined

        if (token) headers.Authorization = `Bearer ${token}`

        if (abortable) {
            window[ABORT_CONTROLLER] = new AbortController()
            signal = window[ABORT_CONTROLLER].signal
        }

        const payload: any = { method, headers, signal }

        if (body) payload.body = JSON.stringify(body)

        const finalUrl = query ? `${url}?${createURLQueryString(query)}` : url
        const result = await fetch(finalUrl, payload)
        return returnParser(result)
    } catch (e) {
        logger('Error in fetched:', e)
    }
}

export const avatars = [
    'https://randomuser.me/api/portraits/men/50.jpg',
    'https://randomuser.me/api/portraits/men/51.jpg',
    'https://randomuser.me/api/portraits/men/52.jpg',
    'https://randomuser.me/api/portraits/men/53.jpg',
    'https://randomuser.me/api/portraits/men/54.jpg',
    'https://randomuser.me/api/portraits/men/55.jpg',
    'https://randomuser.me/api/portraits/men/56.jpg',
    'https://randomuser.me/api/portraits/men/57.jpg',
    'https://randomuser.me/api/portraits/men/58.jpg',
    'https://randomuser.me/api/portraits/men/59.jpg',
    'https://randomuser.me/api/portraits/men/60.jpg',
    'https://randomuser.me/api/portraits/men/61.jpg',
    'https://randomuser.me/api/portraits/men/62.jpg',
    'https://randomuser.me/api/portraits/men/63.jpg',
    'https://randomuser.me/api/portraits/men/64.jpg',
    'https://randomuser.me/api/portraits/men/65.jpg',
    'https://randomuser.me/api/portraits/men/66.jpg',
    'https://randomuser.me/api/portraits/men/67.jpg',
    'https://randomuser.me/api/portraits/men/68.jpg',
    'https://randomuser.me/api/portraits/men/69.jpg',
]

export const icons = [
    'bell',
    'pen',
    'light',
    'flag',
    'smile',
    'shield',
    'smartphone',
    'hash',
    'map',
    'cloud',
    'radio',
    'box',
    'megaphone',
    'fire',
    'heart',
    'music',
    'photo',
    'star',
    'nice-check',
    'book',
    'camera',
    'globe',
    'clock',
    'anchor',
    'tag',
    'bookmark',
    'carfan',
    'airballoon',
    'chart',
    'rocket',
    'at',
    'pin',
]

export const colors = [...COLORS]

// unused?
// export const navigateToApp = () => {
//     const { search } = window.location
//     const parsed: any = queryString.parse(search)
//     const { redirect } = parsed
//     window.location.href = window.location.origin + (redirect ? redirect : '/app')
// }

export const logger = (...attrs) => {
    if (NODE_ENV == 'development') {
        console.log(...attrs)
    }
}

export const getLastUrlPath = () => {
    const urlParts = window.location.href.split('?')[0].split('/')
    return urlParts[urlParts.length - 1]
}

export const createURLQueryString = (parameters: any): string => {
    const params: any[] = []
    for (const parameter in parameters) {
        if (parameters[parameter] != undefined && parameters[parameter] != null) {
            // If it's a string array, then flatten it
            const value = Array.isArray(parameters[parameter]) ? parameters[parameter].join(',') : parameters[parameter]
            params.push(parameter + '=' + value)
        }
    }
    return params.join('&')
}

export const shouldRedirecToAuth = () =>
    !window.location.href.includes('confirm-email') && !window.location.href.includes('/p/')

export const returnParser = async (result: any): Promise<any> => {
    try {
        if (result.status != 200 && result.status != 201 && result.status != 204) {
            // Manually catch a 401 for the JWT
            // 403 is reserved for the external token validation
            // and the security (scopes) plugin
            switch (result.status) {
                case 401:
                    localStorage.removeItem('FRONTEER_JWT')
                    if (shouldRedirecToAuth()) window.location.href = window.location.origin + '/auth'
                    throw 'There has been a permissions error'
                case 403:
                    throw 'There has been a permissions error'
                case 409:
                    throw 'This resource already exists'
                case 404:
                    throw 'Sorry, not found'
                case 500:
                    throw 'There has been an error'
                default:
                    throw 'Something has gone wrong (' + result.status + ')'
            }
        } else {
            if (result.headers.get('content-type')) {
                return result.json()
            } else {
                return result
            }
        }
    } catch (error) {
        throw error
    }
}

// Imported

export const getProjectIdFromUrl = () => {
    const { pathname } = window.location
    const pathnameParts = pathname.split('/')
    const projectIndex = pathnameParts.indexOf('c')

    // If there is no project in the url
    if (projectIndex == -1) return null

    // Otherwise return the next value (which would be the projectId)
    return pathnameParts[projectIndex + 1]
}

export const deleteDraggableElement = (id) => {
    const elementId = 'temp' + id
    const draggedElement: any = document.getElementById(elementId)

    draggedElement.remove()
}

export const createDraggableElement = (e, id) => {
    const element = e.target

    // Create our element
    const rect = element.getBoundingClientRect()
    const { width, height } = rect
    const div = document.createElement('div')
    const el = element.cloneNode(true)

    // Set the container dimensions
    div.style.display = 'block'
    div.style.backgroundColor = 'white'
    div.style.position = 'absolute'
    div.style.borderRadius = '10px'
    div.style.overflow = 'hidden'
    div.style.width = width + 'px'
    div.style.height = height + 'px'
    div.id = 'temp' + id

    // Create the element
    div.appendChild(el)
    document.body.appendChild(div)

    // Set the drag element
    e.dataTransfer.setDragImage(div, 0, 0)
}

export const privateProjectWithSameMembers = (projects, users) => {
    const userIdArrayString = users
        .map((user) => user.id)
        .sort()
        .join('')
    const privateProjects = projects.filter((project) => project.private)
    const existingProjects = privateProjects.filter((project) => {
        const projectMembersUserIdArrayString = project.members
            .map((member) => member.user.id)
            .sort()
            .join('')

        if (projectMembersUserIdArrayString == userIdArrayString) {
            return true
        } else {
            return false
        }
    })

    return existingProjects.length == 0 ? null : existingProjects[0]
}

export function hex2(c) {
    c = Math.round(c)
    if (c < 0) c = 0
    if (c > 255) c = 255

    var s = c.toString(16)
    if (s.length < 2) s = '0' + s

    return s
}

export function color(r, g, b) {
    return '#' + hex2(r) + hex2(g) + hex2(b)
}

export function shade(col, light) {
    var r = parseInt(col.substr(1, 2), 16)
    var g = parseInt(col.substr(3, 2), 16)
    var b = parseInt(col.substr(5, 2), 16)

    if (light < 0) {
        r = (1 + light) * r
        g = (1 + light) * g
        b = (1 + light) * b
    } else {
        r = (1 - light) * r + light * 300
        g = (1 - light) * g + light * 300
        b = (1 - light) * b + light * 300
    }

    return color(r, g, b)
}

export const getProjectOtherUsers = (members, userId) => {
    let users = []
    members.map((member) => {
        if (member.user.id != userId) users.push(member.user)
    })
    return users
}

export const getProjectOtherUser = (members, userId) => {
    let user = {}
    members.map((member) => {
        if (member.user.id != userId) user = member.user
    })
    return user
}

export const getPrivateProjectAvatar = (members, userId) => {
    if (members.length == 2) {
        const otherUser: any = getProjectOtherUser(members, userId)
        const { name, image, status } = otherUser
        return { name, image, status }
    } else {
        return { name: members.length, image: undefined, status: undefined }
    }
}

export const getUsersWithoutMe = (users, userId) => users.filter((user: any) => user.id != userId)

/* 
export const getUserGroupName = (users: UserSelectUser[]) => {
    switch (users.length) {
        case 0:
            return ''
        case 1:
            return `You & ${users[0].name.split(' ')[0]}`
        case 2:
            return `You, ${users[0].name.split(' ')[0]} & ${users[1].name.split(' ')[0]}`
        default:
            return `You, ${users[0].name.split(' ')[0]}, ${users[1].name.split(' ')[0]} & ${users.length - 2} more`
    }
} */

export const getUserGroupName = (users: any = []) => {
    if (!users) return ''
    switch (users.length) {
        case 0:
            return ''
        case 1:
            return `You & ${users[0].name.split(' ')[0]}`
        case 2:
            return `You, ${users[0].name.split(' ')[0]} & ${users[1].name.split(' ')[0]}`
        default:
            return `You, ${users[0].name.split(' ')[0]}, ${users[1].name.split(' ')[0]} & ${users.length - 2} more`
    }
}

export const getPrivateProjectImage = (members, userId) => {
    const visibleMembers = members.filter((member, index) => index <= 1 && member.user.id != userId)
    const andMore = members.length - visibleMembers

    if (andMore > 0) {
        return visibleMembers.join(', ')
    } else {
        return `${visibleMembers.join(', ')} & ${andMore} more`
    }
}

export const getPrivateProjectName = (members, userId) => {
    const visibleMembers = members.filter((member, index) => index <= 1 && member.user.id != userId)
    const andMore = members.length - visibleMembers

    if (andMore > 0) {
        return visibleMembers.join(', ')
    } else {
        return `${visibleMembers.join(', ')} & ${andMore} more`
    }
}

export const getProjectNameWithMembers = (members, userId) => {
    const usernames: any = []
    members.map((member) => {
        if (member.user.id != userId) usernames.push(member.user.name.split(' ')[0])
    })
    return usernames.join(', ')
}

export const getUsersThatAreStillTyping = (typing) => {
    const snapshot = new Date().getTime()
    const usersThatAreStillTyping = []
    typing.map((t) => {
        if (snapshot - t.userTime <= 1000) usersThatAreStillTyping.push(t)
    })
    return usersThatAreStillTyping
}

export const notifyProjectOfTyping = (projectId, userName) => {
    /* MessagingService.getInstance().sync(projectId, {
        type: USER_IS_TYPING,
        payload: { projectId, userName },
    }) */
}

export const doNotDisturbUser = (user) => {
    const { timezone, dnd, dndUntil } = user

    if (dnd && !!dndUntil) {
        const currentDate = new Date().getTime()
        const dndUntilDate = new Date(dndUntil).getTime() //.tz(timezone)
        const currentDateIsBeforeDndDate = currentDate < dndUntilDate

        // true if it's
        // false if it's elapsed
        return currentDateIsBeforeDndDate
    } else {
        return false
    }
}

export const shouldShowUnreadNotification = (projectNotifications, projectId, mention) => {
    const projectNotification = projectNotifications.filter(
        (projectNotification) => projectNotification.projectId == projectId
    )[0]

    // If there are nonoe - then YES
    if (!projectNotification) return true

    // Otherwise
    const { every } = projectNotification

    // Do the checks here
    if (every == PROJECT_NOTIFICATIONS.NONE) return false
    if (every == PROJECT_NOTIFICATIONS.MESSAGES) return true
    if (every == PROJECT_NOTIFICATIONS.MENTIONS) return !!mention
}

export const getUnreadCountForProjectId = (projectUnreads, projectId) => {
    return projectUnreads.filter((projectUnread) => projectUnread.projectId == projectId).length
}

export const getHighestTaskOrder = (tasks) => {
    return tasks.reduce((acc, value) => (value.order > acc ? value.order : acc), tasks.length) + 100
}

export const getPreviousTaskOrder = (tasks, taskId) => {
    // If there are no siblings
    if (tasks.length == 0) return 0

    const lowestOrder = tasks.reduce((acc, task) => (acc.order < task.order ? acc : task), 0).order - 100
    const task = tasks.filter((task) => task.id == taskId)[0]
    let taskIndex = 0
    let order = 0

    // Get the index of this task
    tasks.map((task, index) => {
        if (task.id == taskId) taskIndex = index
    })

    // Get the task after this
    const previousTaskIndex = taskIndex - 1
    const previousTask = tasks[previousTaskIndex]

    // if there is no next task (possibly dragged into the highest position)
    if (!previousTask) {
        order = lowestOrder
    } else {
        const minOrder = previousTask.order
        const maxOrder = task.order
        const difference = (maxOrder - minOrder) / 2

        console.log('Found previous task: ', minOrder, maxOrder, difference)

        order = minOrder + difference
    }

    return order
}

export const getNextTaskOrder = (tasks, taskId) => {
    // If there are no siblings
    if (tasks.length == 0) return 1

    const highestOrder = getHighestTaskOrder(tasks) + 100
    const task = tasks.filter((task) => task.id == taskId)[0]
    let taskIndex = 0
    let order = 0

    // Get the index of this task
    tasks.map((task, index) => {
        if (task.id == taskId) taskIndex = index
    })

    // Get the task after this
    const nextTaskIndex = taskIndex + 1
    const nextTask = tasks[nextTaskIndex]

    // if there is no next task (possibly dragged into the highest position)
    if (!nextTask) {
        order = highestOrder
    } else {
        const minOrder = task.order
        const maxOrder = nextTask.order
        const difference = (maxOrder - minOrder) / 2

        console.log('Found next task: ', minOrder, maxOrder, difference)

        order = minOrder + difference
    }

    return order
}

export const sortTasksByLexorank = (tasks) => {
    if (!tasks) return []

    return tasks.sort((a, b) => a.order - b.order)
}

export const sortTasksByOrder = (tasks) => {
    if (!tasks) return []

    return tasks.sort((a, b) => a.order - b.order)
}

export const sortMessagesByCreatedAt = (messages) => {
    if (!messages) return []
    return messages.sort((left, right) => new Date(left.createdAt) > new Date(right.createdAt))
}

export const findChildTasks = (taskId, tasks) => {
    return tasks
        .filter((task) => task.parentId == taskId)
        .map((parentTask) => {
            return {
                ...parentTask,
                children: sortTasksByOrder(findChildTasks(parentTask.id, tasks)),
            }
        })
}

export function getQueryStringValue(name) {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
    var regex = new RegExp('[\\?&]' + name + '=([^&#]*)'),
        results = regex.exec(location.search)
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '))
}

export function isExtensionOpen() {
    const lastPart = getLastUrlPath().toUpperCase()
    return (
        lastPart == 'messages' ||
        lastPart == 'tasks' ||
        lastPart == 'board' ||
        lastPart == 'calendar' ||
        lastPart == 'files' ||
        lastPart == 'members'
    )
}

export const bytesToSize = (bytes) => {
    var sizes = ['bytes', 'kb', 'mb', 'gb', 'tb']
    if (bytes == 0) return '0 Byte'
    var i = parseInt(String(Math.floor(Math.log(bytes) / Math.log(1024))))
    return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i]
}

export const urlParser = (url) => {
    if (!url) return false
    if (typeof url != 'string') return false

    const match = url.match(/(http[s]?:\/\/.*)/i)
    return match ? match[0].split(' ') : false
}

export const imageUrlParser = (url) => {
    const match = url.match(/(http[s]?:\/\/.*\.(?:png|jpg|svg|jpeg|gif))/i)
    return match ? match[1] : false
}

export const vimeoUrlParser = (url) => {
    const match = url.match(
        /(?:www\.|player\.)?vimeo.com\/(?:projects\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_\-]+)?/i
    )
    return match ? match[1] : false
}

export const youtubeUrlParser = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/
    const match = url.match(regExp)
    return match && match[7].length == 11 ? match[7] : false
}

export const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
}

export const showLocalPushNotification = (title, body) => {
    navigator.serviceWorker.ready.then((register) => {
        const serviceWorkerRegistration = register

        if (serviceWorkerRegistration) {
            serviceWorkerRegistration.showNotification(title, {
                body,
                icon: 'https://weekday-assets.s3-us-west-2.amazonaws.com/logo-transparent.png',
                //image: 'https://weekday-assets.s3-us-west-2.amazonaws.com/logo-transparent.png',
            })
        }
    })
}

export const copyToClipboard = (value) => {
    const tempInput: any = document.createElement('input')
    tempInput.style = 'position: absolute; left: -1000px; top: -1000px;'
    tempInput.value = value
    document.body.appendChild(tempInput)
    tempInput.select()
    document.execCommand('copy')
    document.body.removeChild(tempInput)
}

export const validEmail = (email) => {
    var re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email).toLowerCase())
}

export const decimalToMinutes = (minutes) => {
    var sign = minutes < 0 ? '-' : ''
    var min = Math.floor(Math.abs(minutes))
    var sec = Math.floor((Math.abs(minutes) * 60) % 60)
    return sign + (min < 10 ? '0' : '') + min + ':' + (sec < 10 ? '0' : '') + sec
}

export const highlightMessage = (message, query) => {
    var reg = new RegExp(query, 'gi')
    return message.replace(reg, (str) => {
        return `<strong>${str}<strong>`
    })
}

export const parseMessageMarkdown = (markdown, highlight) => {
    const htmlMessage = '' //marked(markdown, { headerIds: false, mangle: false })
    const compiledMessage = highlight
        ? highlight != ''
            ? highlightMessage(htmlMessage, highlight)
            : htmlMessage
        : htmlMessage

    // What we do here is replace the emoji symbol with one from EmojiOne
    const regex = new RegExp('(:[a-zA-Z0-9-_+]+:(:skin-tone-[2-6]:)?)', 'g')
    const partsOfTheMessageText = []
    let matchArr
    let lastOffset = 0

    // Match all instances of the emoji
    while ((matchArr = regex.exec(compiledMessage)) !== null) {
        const previousText = compiledMessage.substring(lastOffset, matchArr.index)
        if (previousText.length) partsOfTheMessageText.push(previousText)

        lastOffset = matchArr.index + matchArr[0].length

        /* 
      const emoji = ReactDOMServer.renderToStaticMarkup(
        <Emoji
            emoji={matchArr[0]}
            set="emojione"
            size={22}
            fallback={(em, props) => {
                return em ? `:${em.short_names[0]}:` : props.emoji
            }}
            />
      ) */
        const emoji = 'ed'

        if (emoji) {
            partsOfTheMessageText.push(emoji)
        } else {
            partsOfTheMessageText.push(matchArr[0])
        }
    }

    const finalPartOfTheText = compiledMessage.substring(lastOffset, compiledMessage.length)

    if (finalPartOfTheText.length) partsOfTheMessageText.push(finalPartOfTheText)

    // Finally set the message after processnig
    return partsOfTheMessageText.join('')
}

export const sendFocusComposeInputEvent = () => {
    //EventService.getInstance().emit(FOCUS_COMPOSE_INPUT, null)
}

export const shortenMarkdownText = (text) => {
    const maxWords = 12
    const html = '' //`<p>${marked(text, { headerIds: false, mangle: false })}</p>`
    const div = document.createElement('div')

    div.innerHTML = html

    const plainText = div.innerText
    const textArray = plainText.split(' ').filter((_, i) => i < maxWords)

    return `${textArray.join(' ')}...`
}

export const isElectron = () => {
    const userAgent = navigator.userAgent.toLowerCase()

    return userAgent.indexOf('electron/') > -1
}

export const getMentions = (text) => {
    const mentions = text
        .replace('/./g', ' ')
        .replace('/,/g', ' ')
        .split(' ')
        .filter((part) => part[0] == '@')
    return mentions || []
}

export const classNames = (object) => {
    const classArray = []

    for (let property in object) {
        if (object[property]) classArray.push(property)
    }

    return classArray.join(' ')
}

export const isTaskHeading = (title) => {
    return title[title.length - 1] == ':'
}

export const generateInitials = (str) => {
    return str
        .split(' ')
        .map((part, _) => {
            return part[0] ? part[0].toUpperCase() : ''
        })
        .splice(0, 2)
        .toString()
        .replace(',', '')
        .trim()
}

export const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)

// --------------------

export const names = [
    'Fronteer',
    'Yack',
    'Acoustic',
    'Fold',
    'Fronteer',
    'Yreeka',
    'Playfirst',
    'Hatch',
    'Deny The Empire',
]

export const sparkline = [
    Math.random(),
    Math.random(),
    Math.random(),
    Math.random(),
    Math.random(),
    Math.random(),
    Math.random(),
    Math.random(),
    Math.random(),
    Math.random(),
    Math.random(),
    Math.random(),
    Math.random(),
    Math.random(),
]

export const getWorkspaces = () => {
    return [
        { id: 123, name: 'Fold', image: 'https://avatars.githubusercontent.com/u/111084337?s=96&v=4' },
        { id: 456, name: 'Fronteer', image: 'https://avatars.githubusercontent.com/u/114044579?s=96&v=4' },
        { id: 789, name: 'PDFasaurus', image: 'https://avatars.githubusercontent.com/u/83326539?s=200&v=4' },
        { id: 0, name: 'Fronteer', image: null },
    ]
}

export const getSearchResults = () => {
    return []
}

export const getWorkspace = () => {
    return {
        id: 234,
        name: 'Fronteer',
        image: 'https://avatars.githubusercontent.com/u/111084337?s=96&v=4',
    }
}

export const getWorkspaceMembers = () => {
    return []
}

export const getWorkspaceSettings = () => {
    return {}
}

export const getWorkspaceInbox = () => {
    return []
}

export const getWorkspaceCalendar = () => {
    return []
}

export const getWorkspaceTasks = () => {
    return []
}

export const getStarred = () => {
    return [
        { ...getProject(), type: 'project' },
        { ...getProject(), type: 'project' },
        { ...getProject(), type: 'project' },
        { ...getConversation(), type: 'conversation' },
    ]
}

export const getTeam = () => {
    return {
        id: 234,
        name: 'Design',
        color: Token.ColorPink400,
        archived: false,
        starred: true,
        projects: [getProject(), getProject(), getProject()],
    }
}

export const getTeams = () => {
    return [
        getTeam(),
        {
            id: 234,
            name: 'Engineering',
            color: Token.ColorBlue400,
            archived: false,
            starred: true,
            projects: [getProject()],
        },
        {
            id: 234,
            name: 'Finance',
            color: Token.ColorViolet400,
            archived: false,
            starred: true,
            projects: [getProject()],
        },
    ]
}

export const getConversation = () => {
    return {
        id: 234,
        archived: false,
        starred: true,
        members: [],
    }
}

export const getConversations = () => {
    return [
        {
            id: 234,
            archived: false,
            starred: true,
            members: [],
        },
        {
            id: 234,
            archived: false,
            starred: true,
            members: [],
        },
    ]
}

export const getProjects = () => {
    return [getProject(), getProject(), getProject(), getProject()]
}

export const getProjectMemberPreview = () => {
    return {
        members: [
            { name: 'Jo du Plessis', image: 'https://randomuser.me/api/portraits/men/31.jpg' },
            { name: 'Jo du Plessis', image: null },
            { name: 'Jo du Plessis', image: 'https://randomuser.me/api/portraits/men/33.jpg' },
        ],
        totalMembers: 35,
    }
}

export const getProjectMembers = () => {
    return []
}

export const getProject = () => {
    return {
        id: 123,
        name: names[Math.round(Math.random() * names.length - 1)] || 'Default',
        linked: Math.random() < 0.2,
        archived: false,
        starred: true,
        exclusive: Math.random() > 0.5,
        color: colors[Math.round(Math.random() * colors.length - 1)],
    }
}

export const getProjectSettings = () => {
    return {}
}

export const getProjectInbox = () => {
    return []
}

export const getProjectChat = () => {
    return []
}

export const getProjectBoard = () => {
    return []
}

export const getProjectList = () => {
    return []
}

export const getProjectCalendar = () => {
    return []
}

export const getProjectFiles = () => {
    return []
}

export const getUser = () => {
    return {
        id: 123,
        name: 'Jo du Plessis',
        image: 'https://randomuser.me/api/portraits/men/31.jpg',
        email: 'jo@joduplessis.com',
        presence: 'online',
        status: {
            text: 'Hi there',
            emoticon: '🤘🏻',
        },
    }
}

export const getUserProfile = () => {
    return {}
}

export const isValidEmail = (email: string): boolean => {
    const re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email).toLowerCase())
}

export const validateEmail = (email: string): boolean => {
    const re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email).toLowerCase())
}

export const findRoleTypeWithRoleId = (roles: any[], roleId: string): string => {
    return roles.reduce((acc, val) => {
        if (val.id == roleId) {
            return acc + val.type
        } else {
            return acc + ''
        }
    }, '')
}

export const stripSpecialChars = (text: string): string => {
    return text ? text.replace(/[`~!@#$%^&*()|+\= ?;:'",.<>\{\}\[\]\\\/]/gi, '') : ''
}
